import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { ENDPOINTS, removeEmptyParams, RenderedSummary, Report } from '@/api';
import { fetcher } from '@/api/fetcher';
import { useUser } from '@/core/user';
import { ML_FINDINGS } from '@/shared/constant';
import { useFormStructure, useStudyGuard } from '@/shared/guard';
import { categorizeFindings, groupFindingsByOrgan, IS_LOCAL_ENV } from '@/shared/util';
import {
  useAIDefaultValuesStore,
  useDefaultFindingStore,
  useFindingStore,
  useReportStore,
} from '@/store';

import { extractMlServerFindings } from './useReportQuery.util';

export const useReportQuery = () => {
  const {
    patient: { gender },
    radiologistScan: { skuID },
    studyId,
  } = useStudyGuard();
  const { user } = useUser();
  const { uuid } = useFormStructure();

  const { findings, setFindings } = useFindingStore();
  const { defaultFindings, setDefaultFindings } = useDefaultFindingStore();
  const { setIsReportLoading, setReport } = useReportStore();
  const [reportData, setReportData] = useState<Report | undefined>();
  const { setAIFindings } = useAIDefaultValuesStore();

  const fetchReport = () =>
    fetcher.FCT.get<Report>(`${ENDPOINTS.FCT.REPORT.STUDY}${studyId}`, {
      params: removeEmptyParams({
        patient_gender: gender,
        study_sku: skuID,
        user_id: user.user_id,
      }),
    });

  const fetchRenderedSummaries = (id: string) =>
    fetcher.FCT.get<RenderedSummary[]>(`${ENDPOINTS.FCT.RENDERED_SUMMARY.BASE}report/${id}`);

  const { data: report, isLoading: isReportLoading } = useQuery({
    enabled: !!studyId && !!uuid && !!skuID && !!user,
    queryFn: fetchReport,
    queryKey: ['report', studyId, user.user_id, uuid],
  });

  const { data: renderedSummaries, isLoading: isSummaryLoading } = useQuery({
    enabled: !!report?.data?.uuid,
    queryFn: () => fetchRenderedSummaries(report!.data.uuid),
    queryKey: ['renderedSummaries', report?.data.uuid],
    staleTime: Infinity,
  });

  useEffect(() => {
    setIsReportLoading(isReportLoading || isSummaryLoading);
  }, [isReportLoading, isSummaryLoading, setIsReportLoading]);

  useEffect(() => {
    if (report) {
      setReport(report.data);
      setReportData(report.data);
    }
  }, [report]);

  useEffect(() => {
    if (renderedSummaries) {
      const renderedSummaryData = IS_LOCAL_ENV
        ? [...(renderedSummaries?.data || []), ...ML_FINDINGS]
        : renderedSummaries?.data;

      const groupedData = groupFindingsByOrgan(renderedSummaryData as RenderedSummary[]);

      const mlServerFindings = extractMlServerFindings(groupedData);

      setAIFindings(mlServerFindings);

      Object.keys(groupedData).forEach((organ) => {
        const { commonFindings, defaultFindings: defaultFindingsData } = categorizeFindings(
          groupedData[organ],
        );

        setFindings({
          ...findings,
          [organ]: commonFindings,
        });

        setDefaultFindings({
          ...defaultFindings,
          [organ]: defaultFindingsData,
        });
      });
    }
  }, [renderedSummaries]);

  return { isLoading: isReportLoading || isSummaryLoading, report: reportData };
};
